import {
  Box,
  Button,
  Checkbox,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import React, { useState } from "react";
import { ColorsConstants, inputColor } from "../cssUtils/constants";
import typography from "../cssUtils/typography";

/**
 * MultiSelectDropdown — outlined button that opens a searchable checkbox list.
 *
 * Props:
 *  - options: Array<{ label: string, value: string }>
 *  - value: string[] — selected values
 *  - onChange: (values: string[]) => void
 *  - placeholder: string — shown when nothing is selected
 *  - searchPlaceholder: string
 *  - error: boolean — red border
 *  - sx — merged into the button styles
 */
const MultiSelectDropdown = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select",
  searchPlaceholder = "Search",
  error = false,
  sx = {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const toggle = (optionValue) => {
    const next = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange?.(next);
  };

  const term = search.trim().toLowerCase();
  const visibleOptions = term
    ? options.filter((o) => String(o.label).toLowerCase().includes(term))
    : options;

  const selectedLabels = options
    .filter((o) => value.includes(o.value))
    .map((o) => o.label);

  return (
    <>
      <Button
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<ExpandMore />}
        sx={{
          borderRadius: "8px",
          ...typography.bodyLarge,
          height: 56,
          width: "100%",
          justifyContent: "space-between",
          textAlign: "left",
          textTransform: "none",
          color: selectedLabels.length ? inputColor : ColorsConstants.primaryGray60,
          border: `1px solid ${
            error ? ColorsConstants.error : ColorsConstants.inputFieldGrey
          }`,
          "&:hover": {
            border: `1px solid ${
              error ? ColorsConstants.error : ColorsConstants.primaryGray50
            }`,
            backgroundColor: "transparent",
          },
          ...sx,
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectedLabels.length ? selectedLabels.join(", ") : placeholder}
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        autoFocus={false}
        PaperProps={{
          style: {
            width: anchorEl ? anchorEl.offsetWidth : undefined,
            maxHeight: 360,
          },
        }}
      >
        <Box sx={{ px: 1.5, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              sx: typography.bodyLarge,
            }}
          />
        </Box>

        {visibleOptions.length === 0 && (
          <MenuItem disabled sx={typography.bodyLarge}>
            No results found
          </MenuItem>
        )}

        {visibleOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => toggle(option.value)}
            sx={{ py: 0 }}
          >
            <Checkbox
              edge="start"
              checked={value.includes(option.value)}
              disableRipple
              sx={{ "&.Mui-checked": { color: ColorsConstants.primaryPurple100 } }}
            />
            <ListItemText
              primary={option.label}
              primaryTypographyProps={{ sx: typography.bodyLarge }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default MultiSelectDropdown;
